// src/api/runs.ts

/**
 * Run API helpers and domain contracts for the Quantik Mind enterprise frontend.
 *
 * This module centralizes:
 * - TypeScript contracts for runs and experiments
 * - run detail retrieval
 * - decision insight retrieval
 * - paginated run listing
 *
 * The file acts as a bridge between backend run payloads and frontend pages
 * that display execution history, selection details, and Quantum decision data.
 */

import { api } from '../utils/apiClient';

/**
 * Experiment details returned as part of a run payload.
 */
export interface Experiment {
  internal_test_id?: number;
  priority_score?: number | null;
  priority_rank?: number | null;
  priority_semantics?: string | null;
  priority_tier?: number | null;
  informative_probability?: number | null;
  failure_probability?: number | null;
  risk_probability?: number | null;
  expected_information_gain?: number | null;
  impact?: number | null;
  risk_areas?: string[] | null;
  risk_area_criticality?: Record<string, string> | null;
  model_version?: string | null;
  assessment_status?: string | null;
  probability_source?: string | null;
  calibration_status?: string | null;
  selection_utility?: number | null;
  business_criticality?: string | null;
  mandatory_critical_coverage?: boolean | null;
  inclusion_rule?: string | null;
  adaptive_probability_threshold?: number | null;
  warning?: string | null;
  training_data_kind?: string | null;
  calibration_method?: string | null;
  feature_schema_version?: string | null;
  activation_mode?: string | null;
  limitation?: string | null;
  target_version?: string | null;
  feature_snapshot_id?: string | null;
  base_context_snapshot_id?: string | null;
  test_feature_snapshot_id?: string | null;
  training_data_provenance?: Record<string, unknown> | null;
  near_miss_state?: string | null;
  near_miss_influence_enabled?: boolean | null;
  /** Unique experiment identifier. */
  id: string;

  /** Backend experiment classification. */
  experiment_type: string;

  /** Service targeted by the experiment. */
  service: string;

  /** Optional service instance identifier. */
  service_instance?: string;

  /** Optional generic instance identifier. */
  instance?: string;

  /**
   * Quantum-inspired selection origin used to explain why the experiment
   * was included in the final selection.
   */
  origin?: 'superposition' | 'entanglement' | 'uncertainty' | string;

  /** Human-readable explanation for experiment selection. */
  selection_reason?: string;

  /** Alternate explanation field returned by some payload variants. */
  reason?: string;

  /** Final experiment probability assigned by the backend engine. */
  final_prob?: number;

  /** Risk score associated with the experiment. */
  risk_score?: number;

  /** Indicates whether the experiment was selected for execution. */
  selected: boolean;

  /** Generic experiment type label used by some payload variants. */
  type: string;

  /** Structured breakdown of the causes that contributed to selection. */
  selection_causes?: {
    /** Selected through superposition logic. */
    superposition?: boolean;

    /** Selected through historical entanglement logic. */
    entanglement_historical?: boolean;

    /** Selected through adaptive entanglement logic. */
    entanglement_adaptive?: boolean;

    /** Selected through uncertainty logic. */
    uncertainty?: boolean;
  };

  /** Final normalized origin label after backend processing. */
  final_origin?: string;
}

export interface RiskCoveragePair {
  covered_pct?: number | null;
  uncovered_pct?: number | null;
}

export interface RiskCoverageSummary {
  overall?: RiskCoveragePair;
  high?: RiskCoveragePair;
  medium?: RiskCoveragePair;
  low?: RiskCoveragePair;
}

export interface BusinessRiskMetrics {
  metric_semantics_version?: string;
  metric_value_sources?: Record<string, string>;
  selection_assessment_kind?: 'informative_evidence_probability' | 'legacy_heuristic' | string;
  highest_informative_probability?: number | null;
  highest_probability_test_id?: number | null;
  highest_probability_test_identifier?: string | null;
  selection_configuration?: {
    requested_tau?: number | null;
    effective_tau?: number | null;
    tau_source?: string;
    default_tau?: number;
    supported_range?: [number, number];
    semantics?: string;
    direction?: string;
  } | null;
  model_details?: {
    model_version?: string | null;
    probability_source?: string | null;
    feature_schema_version?: string | null;
    target_version?: string | null;
    training_data_kind?: string | null;
    warning?: string | null;
    calibration_method?: string | null;
    calibration_status?: string | null;
    activation_mode?: string | null;
  } | null;
  membership_reconciliation?: {
    formula?: string;
    tau?: number;
    population_count?: number;
    mean_probability?: number;
    population_stddev?: number;
    quantile_percent?: number;
    quantile_term?: number;
    distribution_term?: number;
    controlling_term?: string;
    effective_threshold?: number;
    at_or_above_threshold?: number;
    inclusion_rule_counts?: Record<string, number>;
    coverage_guardrail_additions?: number;
    post_selection_removals?: number | null;
    unreconciled_delta?: number;
    final_selected?: number;
    reconciles?: boolean;
  } | null;
  legacy_score_diagnostics?: {
    score_name?: string;
    score_semantics?: string;
    selection_role?: string;
    total_score_mass?: number;
    selected_score_mass?: number;
    selected_score_mass_pct?: number;
    unselected_score_mass?: number;
    unselected_score_mass_pct?: number;
    bands?: Record<string, {
      definition?: string;
      total_score_mass?: number;
      selected_score_mass?: number;
      selected_score_mass_pct?: number | null;
    }>;
    top_quartile_test_count?: number;
    top_quartile_selected_count?: number;
    top_quartile_selected_pct?: number;
  };
  classification_diagnostics?: {
    legacy_dependency_signal_selected_count?: number;
    legacy_dependency_signal_is_membership_cause?: boolean;
    legacy_pre_model_always_green_count?: number;
    legacy_pre_model_always_green_is_final_rf_exclusion_count?: boolean;
    name_similarity_selected_count?: number;
    name_similarity_unselected_count?: number;
    name_similarity_is_behavioral_equivalence?: boolean;
    historically_stable_selected_count?: number;
    historically_stable_unselected_count?: number;
    historical_stability_is_exclusion_cause?: boolean | null;
  };
  mapped_coverage_semantics?: {
    unit?: string;
    coverage_condition?: string;
    overlap_handling?: string;
    criticality_source?: string;
    mapping_contracts?: string[];
    training_data_kinds?: string[];
    independently_customer_reviewed?: boolean | null;
    caveat?: string;
  };
  coverage_gaps?: Array<{
    proposition_id: string;
    proposition_name?: string;
    criticality: string;
    associated_test_count: number;
    associated_tests?: Array<{
      test_id?: number | null;
      test_identifier?: string | null;
      service?: string | null;
      informative_probability?: number | null;
      inclusion_rule?: string | null;
    }>;
    highest_probability_test_id?: number | null;
    highest_probability_test_identifier?: string | null;
    highest_informative_probability?: number | null;
    exclusion_reason?: string | null;
  }>;
  mapped_risk_coverage?: Record<'overall' | 'critical' | 'high' | 'medium' | 'low' | 'unknown', {
    covered_count: number;
    total_count: number;
    covered_pct?: number | null;
    uncovered_areas?: string[];
  }>;
  coverage_gap_counts?: Record<'critical' | 'high' | 'medium' | 'low' | 'unknown', number>;
  criticality_conflicts?: Record<string, string[]>;
  observed_risk?: number;
  total_risk?: number;
  selected_risk?: number;
  residual_risk?: number;
  risk_coverage?: number;
  top_risk_coverage?: number;
  declared_behavior_coverage?: {
    covered_count: number;
    total_count: number;
    covered_pct?: number | null;
    uncovered_areas?: string[];
  };
  critical_risk_area_coverage?: {
    covered_count: number;
    total_count: number;
    covered_pct?: number | null;
    uncovered_areas?: string[];
  };
  high_criticality_area_coverage?: {
    covered_count: number;
    total_count: number;
    covered_pct?: number | null;
    uncovered_areas?: string[];
  };
  medium_criticality_area_coverage?: {
    covered_count: number; total_count: number; covered_pct?: number | null; uncovered_areas?: string[];
  };
  low_criticality_area_coverage?: {
    covered_count: number; total_count: number; covered_pct?: number | null; uncovered_areas?: string[];
  };
  unknown_criticality_area_coverage?: {
    covered_count: number; total_count: number; covered_pct?: number | null; uncovered_areas?: string[];
  };
  high_risk_coverage?: number | null;
  high_captured_pct?: number | null;
  medium_captured_pct?: number | null;
  low_captured_pct?: number | null;
  high_residual_pct?: number | null;
  medium_residual_pct?: number | null;
  low_residual_pct?: number | null;
  risk_coverage_summary?: RiskCoverageSummary;
  covered_risk_composition?: {
    high?: number;
    medium?: number;
    low?: number;
  };
  uncovered_risk_composition?: {
    high?: number;
    medium?: number;
    low?: number;
  };
  covered_risk_high_pct?: number | null;
  covered_risk_medium_pct?: number | null;
  covered_risk_low_pct?: number | null;
  uncovered_risk_high_pct?: number | null;
  uncovered_risk_medium_pct?: number | null;
  uncovered_risk_low_pct?: number | null;
  test_budget?: number;
  tests_reduced?: number;
  risk_efficiency?: number;
  avg_selected_risk?: number;
  avg_skipped_risk?: number;
  always_green_tests_skipped?: number | null;
  redundant_tests_avoided?: number | null;
  dependency_aware_tests_added?: number | null;
  residual_risk_profile?: {
    high?: number;
    medium?: number;
    low?: number;
  };
  residual_risk_quality_label?: string;
}

/**
 * Summary run payload returned when listing runs.
 *
 * This shape is intentionally lighter than the full run detail payload and is
 * used by dashboard and history pages.
 */
export interface RunSummary {
  /** Unique run identifier. */
  run_id: string;

  /** Project to which the run belongs. */
  project_id: string;

  /** Run creation timestamp. */
  created_at: string;

  /** Timestamp when an asynchronous selection request was accepted. */
  queued_at?: string | null;

  /** Timestamp when synchronous or worker execution started. */
  started_at?: string | null;

  /** Timestamp when execution completed. */
  completed_at?: string | null;

  /** Total number of experiments considered by the engine. */
  total_experiments: number;

  /** Number of experiments selected for execution. */
  selected_experiments: number;

  /** Optional run classification label. */
  run_type?: string;
  run_source?: string;
  history_kind?: 'selection' | 'imported_execution_history' | string;
  selection_applicable?: boolean;

  /** Optional precomputed reduction percentage returned by the backend. */
  reduction_percentage?: number | null;

  /** Optional risk coverage achieved by the final selection. */
  risk_coverage?: number;
  test_coverage?: number;
  risk_efficiency?: number;
  top25_risk_share?: number;

  /**
   * Coverage of the top-quartile risk band by selected tests.
   * Populated by the backend when available; null or undefined means N/A in the table.
   */
  top_risk_coverage?: number | null;
  high_risk_coverage?: number | null;

  /** Optional execution status of the run. */
  status?: 'running' | 'completed' | 'failed';
}

/**
 * Detailed run payload returned by the backend.
 */
export interface RunDetail {
  detail_view?: string;
  detail_page?: { page: number; page_size: number; total: number; filtered_count: number; total_pages: number; unavailable_records?: number };
  near_miss_explanation?: import('./nearMiss').SavedNearMissExplanationData | null;
  /** Unique run identifier. */
  run_id: string;
  run_source?: string | null;
  history_kind?: 'selection' | 'imported_execution_history' | string;
  selection_applicable?: boolean;

  /** Project to which the run belongs. */
  project_id: string;

  /** Run creation timestamp. */
  created_at: string;

  /** Total number of experiments considered by the engine. */
  total_experiments: number;

  /** Number of experiments selected for execution. */
  selected_experiments: number;

  /** Percentage of tests not executed. */
  reduction_percentage?: number | null;

  /** Risk coverage achieved by the final selection. */
  risk_coverage: number;

  /** Optional total risk value for the run. */
  risk_total?: number;

  /** Optional sum of selected risk values. */
  selected_risk_sum?: number;

  /** Quantum Selection Index or equivalent quality metric. */
  qsi?: number;

  /** Confidence Index or equivalent quality metric. */
  ci?: number;

  /** Mean Risk Reduction or equivalent optimization metric. */
  mrr?: number;

  /** Number of always-green tests avoided. */
  avoided_always_green?: number;

  /** Percentage of always-green tests avoided. */
  avoided_ag_pct?: number;

  /** Saved redundancy value for the run. */
  redundancy_saved?: number | null;

  /** Count of redundant tests avoided, derived from per-row selection flags. */
  redundant_tests_avoided?: number | null;
  dependency_aware_tests_added?: number | null;

  /** Share of risk concentrated in the top 25 percent of selections. */
  top25_risk_share?: number;

  /** Percentage of high-risk signal captured by this selection. */
  high_risk_coverage?: number | null;

  /** Test coverage reported for the run. */
  test_coverage?: number;

  /** Efficiency of the selected risk relative to executed tests. */
  risk_efficiency?: number;

  /** Frozen adaptive selection breadth and its per-run provenance. */
  tau?: number | null;
  selection_configuration?: BusinessRiskMetrics['selection_configuration'];

  business_metrics?: BusinessRiskMetrics;

  /** Experiments associated with the run. */
  experiments: Experiment[];

  /** Optional execution status of the run. */
  status?: 'running' | 'completed' | 'failed';
}

export type RunLifecycleStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface RunStatus {
  run_id: string;
  project_id: string;
  run_type: string;
  status: RunLifecycleStatus;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  failed_at?: string | null;
  cancelled_at?: string | null;
  execution_time_ms?: number | null;
  error_message?: string | null;
  result_available: boolean;
  result_url?: string | null;
  total_tests: number;
  selected_tests: number;
  reduction_percentage?: number | null;
  risk_coverage?: number | null;
}

/**
 * Decision insight payload returned by the functional runs API.
 */
export interface DecisionInsight {
  /** Always-green test avoidance metrics, when available. */
  always_green?: {
    /** Number of always-green tests avoided. */
    avoided: number;

    /** Percentage of always-green tests avoided. */
    avoided_percentage?: number;
  };
}

/**
 * Paginated response returned when listing runs.
 */
export interface PaginatedRuns {
  /** Total number of available runs matching the query. */
  total: number;

  /** Page data returned by the backend. */
  data: RunSummary[];
}

export interface SkippedTestsSummary {
  run_id: string;
  total_candidates: number;
  selected_count: number;
  skipped_count: number;
  total_skipped_count?: number;
  always_green_avoided_count: number;
  redundant_tests_avoided_count: number;
  high_risk_skipped_count: number;
  medium_risk_skipped_count: number;
  low_risk_skipped_count: number;
  avg_skipped_dynamic_risk_signal?: number | null;
  avg_skipped_execution_value?: number | null;
  redundancy_tracking_available?: boolean;
  model_version?: string | null;
  probability_source?: string | null;
  adaptive_probability_threshold?: number | null;
  effective_tau?: number | null;
  near_miss_state?: string | null;
  near_miss_influence_enabled?: boolean | null;
}

export interface SkippedTestRow {
  id: number;
  test_id: number;
  test_case_identifier?: string | null;
  test_type?: string | null;
  service?: string | null;
  dynamic_risk_signal?: number | null;
  execution_value?: number | null;
  final_execution_value?: number | null;
  risk_bucket?: string | null;
  skip_reason?: string | null;
  is_always_green: boolean;
  is_redundant: boolean;
  covered_by_test_id?: number | null;
  covered_by_reason?: string | null;
  business_skip_reason: string;
  informative_probability?: number | null;
  adaptive_probability_threshold?: number | null;
  effective_tau?: number | null;
  inclusion_rule?: string | null;
  risk_areas?: string[] | null;
  risk_area_criticality?: Record<string, string> | null;
  model_version?: string | null;
  probability_source?: string | null;
  near_miss_state?: string | null;
  comparison?: {
    selected_without_influence?: boolean | null;
    probability_without_influence?: number | null;
    threshold_without_influence?: number | null;
  } | null;
}

export interface SkippedTestsAnalysis {
  summary: SkippedTestsSummary;
  residual_risk_profile: {
    high: number;
    medium: number;
    low: number;
    quality_label: string;
  };
  pagination: {
    page: number;
    page_size: number;
    total: number;
    filtered_count?: number;
    total_skipped_count?: number;
    total_pages: number;
  };
  rows: SkippedTestRow[];
}

/**
 * Retrieve the full detail for a specific run.
 *
 * @param runId - Unique identifier of the run to retrieve.
 * @returns Detailed run payload.
 */
export interface RunDetailOptions {
  view: 'summary' | 'selected' | 'near_miss' | 'evidence';
  page?: number; page_size?: number; search?: string; outcome?: string;
  sort_by?: 'priority' | 'experiment_type' | 'service' | 'risk_score' | 'origin';
  descending?: boolean; test_id?: number;
}
export const fetchRunDetail = async (runId: string, options?: RunDetailOptions, signal?: AbortSignal): Promise<RunDetail> => {
  const query = options ? new URLSearchParams(Object.entries(options).map(([key, value]) => [key, String(value)])).toString() : '';
  return api.get<RunDetail>(`/runs/${encodeURIComponent(runId)}${query ? `?${query}` : ''}`, signal ? { signal } : undefined);
};

export const fetchRunStatus = async (runId: string): Promise<RunStatus> => {
  return api.get<RunStatus>(`/runs/${runId}/status`);
};

/**
 * Retrieve additional decision insight metrics for a functional run.
 *
 * @param runId - Unique identifier of the run to inspect.
 * @returns Decision insight payload for the requested run.
 */
export const fetchDecisionInsight = async (
  runId: string, signal?: AbortSignal
): Promise<DecisionInsight> => {
  return api.get<DecisionInsight>(`/functional/runs/${runId}/decision-insight`, signal ? { signal } : undefined);
};

/**
 * Retrieve a paginated list of runs.
 *
 * The backend currently supports filtering by project, limit, and offset.
 * The optional runType argument is forwarded to the backend as the
 * `run_type` query parameter when provided.
 *
 * @param projectId - Optional project identifier used to scope the result set.
 * @param limit - Maximum number of runs to return.
 * @param offset - Pagination offset.
 * @param runType - Optional run classification filter reserved for future use.
 * @returns Paginated run listing.
 */
export const fetchAllRuns = async (
  projectId?: string,
  limit: number = 100,
  offset: number = 0,
  runType?: 'chaos' | 'functional',
  includeImportedHistory: boolean = false
): Promise<PaginatedRuns> => {
  const params = new URLSearchParams();

  if (projectId) {
    params.append('project_id', projectId);
  }

  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  if (runType) {
    params.append('run_type', runType);
  }

  params.append('include_imported_history', String(includeImportedHistory));

  const data = await api.get<PaginatedRuns>(`/runs/?${params.toString()}`);
  return data;
};

export const fetchSkippedTestsAnalysis = async (
  runId: string,
  params: {
    page?: number;
    page_size?: number;
    risk_bucket?: string;
    service?: string;
    is_always_green?: boolean;
    is_redundant?: boolean;
  } = {},
  signal?: AbortSignal
): Promise<SkippedTestsAnalysis> => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });

  return api.get<SkippedTestsAnalysis>(
    `/runs/${runId}/skipped-tests?${search.toString()}`, signal ? { signal } : undefined
  );
};
